import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGetAssessmentsAccumulationMappingsQueryKey,
  useCreateAccumulationTagAccumulation,
  useDeleteAccumulationTagAccumulation,
} from '@/entities/assessments/api/generated/api';
import { invalidateByPathPrefix } from '@/shared/lib/query/invalidateByPathPrefix';
import { toast } from '@/shared/ui';

type Params = {
  assessmentsAccumulationId: string;
  initialTagIds: string[];
};

const stableKey = (ids: readonly string[]) => [...ids].sort().join(',');

/**
 * 査定積算へのタグ紐付けを即時保存するフック
 *
 * 楽観的更新: ローカル state を即座に更新し、差分を POST/DELETE する。
 * 全ミューテーション成功時に成功トースト、失敗時はロールバック + エラートースト。
 */
export const useAccumulationTagAssignment = ({
  assessmentsAccumulationId,
  initialTagIds,
}: Params) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialTagIds);

  // initialTagIds は呼び出し元で毎レンダー新しい配列が生成され得る。
  // stableKey で内容差分のみを検知し、参照変化のみのケースは同期しない。
  const lastSyncedRef = useRef<string>(stableKey(initialTagIds));
  useEffect(() => {
    const next = stableKey(initialTagIds);
    if (next !== lastSyncedRef.current) {
      lastSyncedRef.current = next;
      setSelectedTagIds(initialTagIds);
    }
  }, [initialTagIds]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getGetAssessmentsAccumulationMappingsQueryKey(assessmentsAccumulationId),
    });
    invalidateByPathPrefix(queryClient, { include: ['/assessments/comparisons'] });
  };

  const { mutate: addAssignment } = useCreateAccumulationTagAccumulation({
    request: { suppressToast: true },
  });
  const { mutate: removeAssignment } = useDeleteAccumulationTagAccumulation({
    request: { suppressToast: true },
  });

  const handleChange = (nextTagIds: string[]) => {
    const previous = selectedTagIds;
    const previousSet = new Set(previous);
    const nextSet = new Set(nextTagIds);
    const added = nextTagIds.filter((id) => !previousSet.has(id));
    const removed = previous.filter((id) => !nextSet.has(id));

    if (added.length === 0 && removed.length === 0) return;

    setSelectedTagIds(nextTagIds);

    let pending = added.length + removed.length;
    let hasError = false;

    const onSettled = () => {
      pending--;
      if (pending > 0) return;
      if (hasError) {
        invalidate();
        return;
      }
      const message =
        added.length > 0 && removed.length === 0
          ? t('tag.messages.assignSuccess')
          : added.length === 0 && removed.length > 0
            ? t('tag.messages.unassignSuccess')
            : t('tag.messages.updateSuccess');
      toast.success(message);
      invalidate();
    };

    const onError = () => {
      if (!hasError) {
        hasError = true;
        setSelectedTagIds(previous);
        toast.error(t('common.messages.update-failed', { name: t('assessment.memo.tag') }));
      }
    };

    for (const accumulationTagId of added) {
      addAssignment({ assessmentsAccumulationId, accumulationTagId }, { onError, onSettled });
    }
    for (const accumulationTagId of removed) {
      removeAssignment({ assessmentsAccumulationId, accumulationTagId }, { onError, onSettled });
    }
  };

  return { selectedTagIds, handleChange };
};
